package org.catharsis.spendtel.feature.presentation.publicshare

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.catharsis.spendtel.feature.data.formatCurrency
import org.catharsis.spendtel.feature.legal.LegalConstants
import org.catharsis.spendtel.feature.presentation.PublicShareViewModel
import org.catharsis.spendtel.feature.presentation.expensereport.GradientHeroCard
import org.catharsis.spendtel.feature.presentation.expensereport.SoftInsightCard
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelGradients
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelReportColors
import org.catharsis.spendtel.feature.presentation.expensereport.accentForIndex
import org.catharsis.spendtel.feature.share.PublicShareDocument
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@Composable
fun PublicShareScreen(
    shareId: String,
    modifier: Modifier = Modifier,
) {
    val viewModel = koinViewModel<PublicShareViewModel>(parameters = { parametersOf(shareId) })
    val state by viewModel.state.collectAsState()

    Box(
        modifier = modifier
            .fillMaxSize()
            .padding(24.dp),
        contentAlignment = Alignment.TopCenter,
    ) {
        when {
            state.isLoading -> {
                CircularProgressIndicator(color = SpendTelReportColors.violet)
            }

            state.error != null -> {
                Text(
                    text = state.error.orEmpty(),
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodyLarge,
                )
            }

            state.share != null -> {
                PublicShareContent(
                    share = state.share!!,
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }
    }
}

@Composable
private fun PublicShareContent(
    share: PublicShareDocument,
    modifier: Modifier = Modifier,
) {
    val snapshot = share.snapshot
    Column(
        modifier = modifier.verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(24.dp),
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text(
                text = "Shared ${LegalConstants.PRODUCT_NAME} story",
                style = MaterialTheme.typography.labelLarge,
                color = SpendTelReportColors.violet,
                fontWeight = FontWeight.SemiBold,
            )
            snapshot.periodLabel?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.bodyMedium,
                    color = SpendTelReportColors.inkMuted,
                )
            }
            snapshot.goalUsed?.let {
                Text(
                    text = "Goal: $it",
                    style = MaterialTheme.typography.bodyMedium,
                    color = SpendTelReportColors.inkMuted,
                )
            }
        }

        snapshot.personalityLabel?.let { label ->
            GradientHeroCard(gradient = SpendTelGradients.hero) {
                Text(
                    text = "Financial personality",
                    style = MaterialTheme.typography.labelMedium,
                    color = Color.White.copy(alpha = 0.75f),
                )
                Text(
                    text = label,
                    style = MaterialTheme.typography.headlineMedium,
                    fontWeight = FontWeight.Bold,
                    color = Color.White,
                )
                snapshot.personalityDescription?.let { description ->
                    Text(
                        text = description,
                        style = MaterialTheme.typography.bodyLarge,
                        color = Color.White.copy(alpha = 0.9f),
                        lineHeight = 24.sp,
                    )
                }
            }
        }

        snapshot.headline?.let { headline ->
            SoftInsightCard(accent = SpendTelReportColors.amber) {
                Text(
                    text = headline,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    color = SpendTelReportColors.ink,
                    lineHeight = 30.sp,
                )
            }
        }

        snapshot.highlightPreview?.let { highlight ->
            SoftInsightCard(accent = SpendTelReportColors.violet) {
                Text(
                    text = highlight,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = SpendTelReportColors.ink,
                    lineHeight = 28.sp,
                )
            }
        }

        snapshot.equivalencePreview?.let { equivalence ->
            GradientHeroCard(gradient = SpendTelGradients.storyWarm) {
                Text(
                    text = equivalence.sourceLabel.uppercase(),
                    style = MaterialTheme.typography.labelMedium,
                    color = Color.White.copy(alpha = 0.75f),
                )
                Text(
                    text = equivalence.comparison,
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold,
                    color = Color.White,
                )
                if (equivalence.insight.isNotBlank()) {
                    Text(
                        text = equivalence.insight,
                        style = MaterialTheme.typography.bodyLarge,
                        color = Color.White.copy(alpha = 0.92f),
                    )
                }
            }
        }

        snapshot.highlights.drop(1).forEachIndexed { index, highlight ->
            SoftInsightCard(accent = accentForIndex(index)) {
                Text(
                    text = highlight,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = SpendTelReportColors.ink,
                )
            }
        }

        snapshot.equivalences.drop(1).forEachIndexed { index, equivalence ->
            SoftInsightCard(accent = accentForIndex(index + 1)) {
                Text(
                    text = equivalence.comparison,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = SpendTelReportColors.ink,
                )
                Text(
                    text = formatCurrency(equivalence.amount, "USD"),
                    style = MaterialTheme.typography.bodyMedium,
                    color = SpendTelReportColors.inkMuted,
                )
            }
        }

        Text(
            text = "This is a redacted preview shared from ${LegalConstants.PRODUCT_NAME}. " +
                "It does not include statement files or individual transactions.",
            style = MaterialTheme.typography.bodySmall,
            color = SpendTelReportColors.inkMuted,
            lineHeight = 20.sp,
        )
    }
}
